import { SSE_EVENTS } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Box, Center, Group, Loader, Stack, Text, Title } from "@mantine/core"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import type { IVisual } from "../../../components/TTRPGReports/interfaces"
import { useAuth } from "../../../hooks/useAuth"
import { useCompanyQuery } from "../../../hooks/useCompanyQuery"
import { useImageLightbox } from "../../../hooks/useImageLightbox"
import { createReconnectingEventSource } from "../../../hooks/useSSE"
import type { IMeetingVisualContent } from "../../../lib/api"
import { fetchVisuals } from "../../../lib/api"
import { queryKeys } from "../../../lib/query-keys"
import { detectProduct } from "../../../lib/tenant"
import { voxcanonOnlyRedirect } from "../voxcanon-only"
import styles from "./viewer.module.css"

// ---------------------------------------------------------------------------
// SSE hook — listens for image_revealed events on this meeting
// ---------------------------------------------------------------------------

interface IRevealedEvent {
  artifactId: number
  content: IMeetingVisualContent
}

function hasUrl(value: unknown): value is { url: string } {
  return typeof value === "object" && value !== null && "url" in value && typeof value.url === "string"
}

function isRevealedEvent(value: Record<string, unknown>): value is IRevealedEvent & Record<string, unknown> {
  return typeof value.artifactId === "number" && hasUrl(value.content)
}

/**
 * Subscribes to the company SSE feed and surfaces `image_revealed` events for
 * this meeting. Routes through the shared reconnecting wrapper so a pod restart
 * re-subscribes with backoff (the previous implementation pointed at a
 * non-existent `/api/sse` endpoint with a `{ event, data }` envelope the backend
 * never emits — the real feed is flat `{ type, meetingId, ... }`).
 */
function useImageRevealedSSE(meetingId: string, onRevealed: (evt: IRevealedEvent) => void): void {
  const { user } = useAuth()
  const companyId = user?.activeOrganizationId
  const callbackRef = useRef(onRevealed)
  callbackRef.current = onRevealed

  useEffect(() => {
    if (!companyId || !meetingId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        const data = parsed as Record<string, unknown>
        if (data.type !== SSE_EVENTS.IMAGE_REVEALED || data.meetingId !== meetingId) return
        if (isRevealedEvent(data)) callbackRef.current({ artifactId: data.artifactId, content: data.content })
      } catch {
        // ignore parse errors
      }
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage)
  }, [companyId, meetingId])
}

// ---------------------------------------------------------------------------
// ViewerPage
// ---------------------------------------------------------------------------

function ViewerPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const { i18n } = useLingui()
  const [extraImages, setExtraImages] = useState<IVisual[]>([])

  const visualsQuery = useCompanyQuery<IVisual[]>({
    // Public per-meeting key (not org-scoped); the wrapper's org id is unused here.
    key: () => queryKeys.ttrpgVisualsPublic(id),
    queryFn: (company) => fetchVisuals(company, id, true),
    refetchInterval: false,
  })

  useImageRevealedSSE(id, (evt) => {
    setExtraImages((prev) => {
      const alreadyKnown =
        prev.some((v) => v.id === evt.artifactId) || (visualsQuery.data ?? []).some((v) => v.id === evt.artifactId)
      if (alreadyKnown) return prev
      const newVisual: IVisual = {
        id: evt.artifactId,
        // Live-revealed images are scenes; the viewer renders by content, not
        // stepId, so this is just a label. (Was the legacy "image-generation".)
        stepId: "image-scenes",
        content: evt.content,
        ordinal: null,
        visibility: "all",
        createdAt: new Date().toISOString(),
      }
      return [...prev, newVisual]
    })
  })

  const allVisuals = [...(visualsQuery.data ?? []), ...extraImages]
  const { openLightbox, LightboxModal } = useImageLightbox()

  return (
    <Box className={styles.root}>
      {/* Header */}
      <Group className={styles.header} justify="space-between">
        <Group gap="sm">
          <Title order={4} c="white">
            <Trans>Voxcanon · Scene Viewer</Trans>
          </Title>
          <Badge color="success" variant="dot" size="sm">
            <Trans>Live</Trans>
          </Badge>
        </Group>
        <Text size="xs" c="dimmed" renderRoot={(props) => <Link to="/meetings/$id" params={{ id }} {...props} />}>
          ← <Trans>Back to session</Trans>
        </Text>
      </Group>

      {/* Content */}
      {visualsQuery.isLoading ? (
        <Center h="60vh">
          <Stack align="center" gap="sm">
            <Loader color="white" />
            <Text c="dimmed" size="sm">
              <Trans>Loading scene images…</Trans>
            </Text>
          </Stack>
        </Center>
      ) : allVisuals.length === 0 ? (
        <Center h="60vh">
          <Stack align="center" gap="sm">
            <Text c="dimmed" size="lg">
              <Trans>Waiting for the DM to reveal scenes…</Trans>
            </Text>
            <Text c="dimmed" size="sm">
              <Trans>Images will appear here as they are revealed</Trans>
            </Text>
          </Stack>
        </Center>
      ) : (
        <div className={styles.gallery}>
          {allVisuals.map((v) => (
            <div key={v.id} className={styles.imageWrapper}>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: image lightbox, ESC handled by Modal */}
              <img
                src={v.content.url}
                alt={v.content.prompt ?? i18n._(msg`Scene`)}
                className={styles.image}
                style={{ cursor: "zoom-in" }}
                onClick={() => openLightbox([{ url: v.content.url, alt: v.content.prompt ?? i18n._(msg`Scene`) }])}
              />
              {v.content.prompt && (
                <div className={styles.caption}>
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {v.content.prompt}
                  </Text>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {LightboxModal}
    </Box>
  )
}

export const Route = createFileRoute("/_auth/meetings/$id_/viewer")({
  // Scene viewer is Voxcanon-only. A Lexicanon URL hit goes back to the meeting
  // (ADR 0004) so the "Waiting for the DM" copy never renders there.
  beforeLoad: ({ params }) => {
    const bounced = voxcanonOnlyRedirect(detectProduct(), { to: "/meetings/$id", params: { id: params.id } })
    if (bounced) throw bounced
  },
  component: ViewerPage,
})
